import { Switch } from '@headlessui/react';
import { useState, useMemo } from 'react';
import { useParams, useOutletContext, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentMap } from '../hooks/useCurrentMap';
import { selectUserRoleForMap } from '../store/selectors';
import { getPermissionsForRole } from '../utils/getPermissionsForRole';
import { DependencyForm } from './DependencyForm';
import { AddEditDeleteBar1 } from '../components_maps/stable/AddEditDeleteBar1';
import { updateObjective } from '../store/map3PendingSlice';
import type { AppDispatch, RootState } from '../store/store';
import type { Dependency1, Result1, MilestoneMap1 } from '../types';
import {
  getDependencyRecords,
  DependencyRecord,
  getDependencyRecordsFromObjective,
} from '../map_dependencies/utils/dependencyUtils';
import { useDependencyRecordsForMapUuid } from '../map_dependencies/hooks/useAllDependencyRecordsForMap';
import {
  useFilteredDependencyRecords,
  ResultTypeFilter,
  LocationFilter,
} from '../map_dependencies/hooks/useFilteredDependencyRecords';
import { RadioGroup, RadioField, Radio } from '../components_catalyst/radio';
import { useAllRelativesData } from '../map_dependencies/hooks/useAllRelativesData';

type OutletContextType = {
  milestoneMapUuid: string;
};

// Configuration type for the component
type DependenciesConfig = {
  showResultsSwitch: boolean;
  showObjectivesSwitch: boolean;
  showMultiMapSwitch: boolean;
  context: 'objective' | 'map';
};

// Custom hook to get milestoneMapUuid from either outlet context or params
function useMilestoneMapUuid(): string {
  const { mapShortId } = useParams();
  const outletContext = useOutletContext<OutletContextType | null>();
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);

  // Prefer outlet context if available
  if (outletContext?.milestoneMapUuid) return outletContext.milestoneMapUuid;

  // If mapShortId is present, resolve to uuid if possible
  if (mapShortId) {
    const found = allMaps.find((m) => m.shortId === mapShortId || m.uuid === mapShortId);
    if (found) return found.uuid;
    // Only return a uuid, never a shortId. Log for debugging.
    console.warn(
      'No milestone map uuid found for shortId:',
      mapShortId,
      '\nMaybe the map is not loaded yet. Consider triggering a fetch or using a hook that returns both uuid and shortId.',
    );
    // TODO: Consider refactoring to useMilestoneMapId that returns { uuid, shortId }
    return '';
  }

  return '';
}

// Custom hook to determine configuration based on route
function useDependenciesConfig(): DependenciesConfig {
  const { objectiveShortId } = useParams();
  const location = useLocation();

  return useMemo(() => {
    const isObjectiveContext = Boolean(objectiveShortId);
    return {
      showResultsSwitch: !isObjectiveContext,
      showObjectivesSwitch: !isObjectiveContext,
      showMultiMapSwitch: !isObjectiveContext,
      context: isObjectiveContext ? 'objective' : 'map',
    };
  }, [objectiveShortId]);
}

// Individual dependency item card
function DependencyListItem({
  dependency,
  onEdit,
  onDelete,
}: {
  dependency: DependencyRecord;
  onEdit: (dependency: DependencyRecord) => void;
  onDelete: () => void;
}) {
  const [isVisible, setIsVisible] = useState(false);

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  const userRole = useSelector((state: RootState) =>
    currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  );
  const { canEdit } = getPermissionsForRole(userRole);

  return (
    <div
      className="flex items-start p-3 rounded-lg border border-gray-200 hover:bg-gray-50 relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium text-gray-900">
          <strong>{dependency.sourceResult.text}</strong>
        </p>
        <p className="text-sm text-gray-500">
          depends on (i.e. cannot be completed without or before)
        </p>
        <p className="text-sm font-medium text-gray-900">
          <strong>{dependency.targetResult.text}</strong>
        </p>
        <p className="text-xs text-gray-500">
          from <span className="italic">{dependency.targetObjective.text}</span>
        </p>
      </div>
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        <AddEditDeleteBar1
          isVisible={isVisible}
          showAdd={false}
          showEdit={canEdit}
          showDelete={canEdit}
          showInfo={false}
          onEdit={() => onEdit(dependency)}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

// List of dependency items
function DependencyList({
  dependencies,
  context,
  milestoneMapUuid,
  milestoneMap,
  config,
  validationError,
}: {
  dependencies: DependencyRecord[];
  context: 'objective' | 'map';
  milestoneMapUuid: string;
  milestoneMap: MilestoneMap1 | undefined;
  config: DependenciesConfig;
  validationError: string | undefined;
}) {
  const [editingDependency, setEditingDependency] = useState<DependencyRecord | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const currentObjective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) =>
      obj.results.some((result) =>
        dependencies.some((dep) => dep.sourceResult.uuid === result.uuid),
      ),
    ),
  );

  const handleEdit = (dependency: DependencyRecord) => {
    setEditingDependency(dependency);
  };

  const handleDelete = async (dependency: DependencyRecord) => {
    if (!currentObjective) return;

    // Find the source result to update
    const sourceResult = currentObjective.results.find(
      (result) => result.uuid === dependency.sourceResult.uuid,
    );

    if (!sourceResult) return;

    // Remove the dependency
    const updatedResult = {
      ...sourceResult,
      dependencies:
        sourceResult.dependencies?.filter(
          (dep) => dep.targetResultUuid !== dependency.targetResult.uuid,
        ) || [],
    };

    // Update the objective with the modified result
    const updatedResults = currentObjective.results.map((result) =>
      result.uuid === sourceResult.uuid ? updatedResult : result,
    );

    await dispatch(
      updateObjective({
        uuid: currentObjective.uuid,
        updates: {
          results: updatedResults,
        },
      }),
    );
  };

  const handleSaveEdit = (updatedDependency: {
    sourceResultUuid: string;
    sourceObjectiveUuid: string;
    targetResultUuid: string;
    targetObjectiveUuid: string;
  }) => {
    // TODO: Implement save logic
    console.log('Save edited dependency:', updatedDependency);
    setEditingDependency(null);
  };

  if (editingDependency) {
    const targetMilestoneMapUuid = milestoneMap ? milestoneMap.uuid : milestoneMapUuid;
    return (
      <DependencyForm
        onSave={handleSaveEdit}
        onCancel={() => setEditingDependency(null)}
        context={config.context}
        initialValues={{
          sourceResultUuid: editingDependency.sourceResult.uuid,
          sourceObjectiveUuid: editingDependency.sourceObjective.uuid,
          targetResultUuid: editingDependency.targetResult.uuid,
          targetObjectiveUuid: editingDependency.targetObjective.uuid,
          targetMilestoneMapUuid: editingDependency.targetMap.uuid,
        }}
        currentMilestoneMapUuid={targetMilestoneMapUuid}
        currentObjectiveUuid={currentObjective?.uuid}
        validationError={validationError}
      />
    );
  }

  return (
    <div className="space-y-2">
      {dependencies.map((dependency) => (
        <DependencyListItem
          key={`${dependency.sourceResult.uuid}-${dependency.targetResult.uuid}`}
          dependency={dependency}
          onEdit={handleEdit}
          onDelete={() => handleDelete(dependency)}
        />
      ))}
    </div>
  );
}

// Reusable toggle switch component
const ToggleSwitch = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Switch.Group>
    <div className="flex items-center justify-between w-full">
      <Switch.Label className="text-sm text-gray-600">{label}</Switch.Label>
      <Switch
        checked={checked}
        onChange={onChange}
        className={`${
          checked ? 'bg-blue-600' : 'bg-gray-200'
        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
      >
        <span
          className={`${
            checked ? 'translate-x-6' : 'translate-x-1'
          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
        />
      </Switch>
    </div>
  </Switch.Group>
);

// Base dependencies component that can be configured
function Map3DependenciesBase({ config }: { config: DependenciesConfig }) {
  const dispatch = useDispatch<AppDispatch>();
  const { objectiveShortId } = useParams();
  const [validationError, setValidationError] = useState<string | undefined>();

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  const userRole = useSelector((state: RootState) =>
    currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  );
  const { canEdit } = getPermissionsForRole(userRole);

  // Filter state
  const [resultTypeFilter, setResultTypeFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('this-map');

  // Get the current map uuid from route/context (always call at top level)
  const milestoneMapUuid = useMilestoneMapUuid();
  // Get the full map object from Redux
  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find(
      (map) => map.uuid === milestoneMapUuid || map.shortId === milestoneMapUuid,
    ),
  );

  // Use the relatives data loader for all dependencies (including cross-map)
  const { loading, allDependencyRecords } = useAllRelativesData(milestoneMapUuid);
  console.log('allDependencyRecords', allDependencyRecords);

  // Get the current objective from Redux store
  const currentObjective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );

  // Get all objectives to resolve target objective details
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  // Get all maps for correct dependency record construction
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);

  // Apply filters
  let filteredDependencies = useFilteredDependencyRecords(allDependencyRecords, {
    resultType: resultTypeFilter as ResultTypeFilter,
    location: locationFilter as LocationFilter,
    mapUuid: milestoneMapUuid,
  });

  // If in 'objective' context, show all dependencies for the current objective's results
  if (config.context === 'objective' && currentObjective) {
    filteredDependencies = getDependencyRecordsFromObjective(
      currentObjective,
      allObjectives,
      milestoneMap,
      allMaps,
    );
  }

  // Toggle states
  const [showResults, setShowResults] = useState(true);
  const [showObjectives, setShowObjectives] = useState(false);
  const [showMultiMap, setShowMultiMap] = useState(false);
  const [isAddingDependency, setIsAddingDependency] = useState(false);

  const handleValidate = (dependency: {
    sourceResultUuid: string;
    sourceObjectiveUuid: string;
    targetResultUuid: string;
    targetObjectiveUuid: string;
  }) => {
    if (!currentObjective) return;

    // Find the source result to check
    const sourceResult = currentObjective.results.find(
      (result) => result.uuid === dependency.sourceResultUuid,
    );

    if (!sourceResult) return;

    // Check if this dependency already exists
    const dependencyExists = sourceResult.dependencies?.some(
      (existingDep) => existingDep.targetResultUuid === dependency.targetResultUuid,
    );

    if (dependencyExists) {
      setValidationError(
        'This dependency already exists. Each result can only depend on another result once.',
      );
    } else {
      setValidationError(undefined);
    }
  };

  const handleSaveDependency = async (dependency: {
    sourceResultUuid: string;
    sourceObjectiveUuid: string;
    targetResultUuid: string;
    targetObjectiveUuid: string;
    targetMilestoneMapUuid: string;
  }) => {
    console.log('onSave called', dependency);
    // Use currentObjective if present and matches, otherwise look up from allObjectives
    let sourceObjective =
      currentObjective && currentObjective.uuid === dependency.sourceObjectiveUuid
        ? currentObjective
        : allObjectives.find((obj: any) => obj.uuid === dependency.sourceObjectiveUuid);
    console.log('Source objective:', sourceObjective);
    if (!sourceObjective) {
      console.error('Source objective not found');
      return;
    }
    // Find source result
    const sourceResult = sourceObjective.results.find(
      (result: any) => result.uuid === dependency.sourceResultUuid,
    );
    console.log('Source result:', sourceResult);
    if (!sourceResult) {
      console.error('Source result not found');
      return;
    }
    // Build new dependency (Dependency1 shape)
    const newDependency = {
      targetResultUuid: dependency.targetResultUuid,
      targetObjectiveUuid: dependency.targetObjectiveUuid,
      targetMapUuid: dependency.targetMilestoneMapUuid,
    };
    console.log('New dependency to add:', newDependency);
    // Add the new dependency
    const updatedResult = {
      ...sourceResult,
      dependencies: [...(sourceResult.dependencies || []), newDependency],
    };
    console.log('Updated result:', updatedResult);
    // Update the objective with the new result
    const updatedResults = sourceObjective.results.map((result: any) =>
      result.uuid === sourceResult.uuid ? updatedResult : result,
    );
    console.log('Updated results array:', updatedResults);
    try {
      await dispatch(
        updateObjective({
          uuid: sourceObjective.uuid,
          updates: {
            results: updatedResults,
          },
        }),
      );
      console.log('Dispatched updateObjective', updatedResults);
    } catch (error) {
      console.error('Error dispatching updateObjective', error);
    }
    setIsAddingDependency(false);
  };

  return (
    <div>
      {/* TODO: Refactor.  Store denormalised DependencyRecord data in Firestore. */}
      {/* Filter UI */}
      {config.context === 'map' && (
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div>
            <div className="font-semibold mb-1">Filter by Type</div>
            <RadioGroup
              value={resultTypeFilter}
              onChange={setResultTypeFilter}
              className="space-y-2"
            >
              <RadioField>
                <Radio value="all" />
                All results
              </RadioField>
              <RadioField>
                <Radio value="key" />
                Key results (milestones) only
              </RadioField>
              <RadioField>
                <Radio value="not-key" />
                Not key results (inchstones) only
              </RadioField>
            </RadioGroup>
          </div>
          <div>
            <div className="font-semibold mb-1">Filter by Location</div>
            <RadioGroup value={locationFilter} onChange={setLocationFilter} className="space-y-2">
              <RadioField>
                <Radio value="all" />
                All
              </RadioField>
              <RadioField>
                <Radio value="cross-map" />
                Cross-map only
              </RadioField>
              <RadioField>
                <Radio value="this-map" />
                This map only
              </RadioField>
            </RadioGroup>
          </div>
        </div>
      )}
      {/* Show loading state if dependencies are being loaded */}
      {loading ? (
        <div className="py-4 text-center text-gray-500">Loading dependencies...</div>
      ) : (
        <DependencyList
          dependencies={filteredDependencies}
          context={config.context}
          milestoneMapUuid={milestoneMapUuid}
          milestoneMap={milestoneMap}
          config={config}
          validationError={validationError}
        />
      )}

      {/* Add dependency section */}
      {isAddingDependency ? (
        <DependencyForm
          onSave={handleSaveDependency}
          onValidate={handleValidate}
          onCancel={() => {
            setIsAddingDependency(false);
            setValidationError(undefined);
          }}
          context={config.context}
          currentMilestoneMapUuid={milestoneMap ? milestoneMap.uuid : milestoneMapUuid}
          currentObjectiveUuid={currentObjective?.uuid}
          validationError={validationError}
        />
      ) : (
        canEdit && (
          <div id="dependencies">
            <button
              type="button"
              onClick={() => setIsAddingDependency(true)}
              className="mt-4 inline-flex items-center rounded-md text-sm text-gray-500 hover:text-gray-900"
            >
              + Add dependency
            </button>
          </div>
        )
      )}
    </div>
  );
}

// Main export that uses route-based configuration
export function Map3Dependencies() {
  const config = useDependenciesConfig();
  return <Map3DependenciesBase config={config} />;
}
